import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { authAPI, plansAPI } from '../services/api';
import { Button, Card } from '../components/ui';
import Loading from '../components/common/Loading';
import { handleApiError } from '../utils/helpers';
import {
  Mail,
  Lock,
  User,
  Building2,
  FileText,
  Image as ImageIcon,
  ArrowRight,
  ArrowLeft,
  Eye,
  EyeOff,
  Layers,
  AlertCircle,
  Home,
  Languages,
  CheckCircle,
  ChevronRight,
  ChevronLeft,
  Sparkles,
  CreditCard,
  Phone
} from 'lucide-react';

const SignupWizard = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { isRTL, t, language, toggleLanguage } = useLanguage();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [plans, setPlans] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Form data
  const [formData, setFormData] = useState({
    // Step 1: Personal Info
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    phone: '',
    // Step 2: Company Info
    company_name: '',
    company_description: '',
    company_logo_url: '',
    // Step 3: Plan Selection
    plan_id: '',
  });

  // Fetch plans on component mount
  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await plansAPI.getAll();
      const allPlans = response.data.plans || [];
      // Filter only active plans
      setPlans(allPlans.filter(p => p.is_active !== false));
      
      // Auto-select free plan (check monthly_price first, then price)
      const freePlan = allPlans.find(p => (p.monthly_price === 0 || p.price === 0) && p.is_active !== false);
      if (freePlan) {
        setFormData(prev => ({ ...prev, plan_id: freePlan.id }));
      }
    } catch (err) {
      console.error('Failed to fetch plans:', err);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError(null);
  };

  const validateStep1 = () => {
    if (!formData.email || !formData.password || !formData.name || !formData.phone) {
      setError(isRTL ? 'يرجى ملء جميع الحقول المطلوبة' : 'Please fill in all required fields');
      return false;
    }

    if (formData.password.length < 12) {
      setError(isRTL ? 'كلمة المرور يجب أن تكون 12 حرفًا على الأقل' : 'Password must be at least 12 characters');
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      setError(isRTL ? 'كلمات المرور غير متطابقة' : 'Passwords do not match');
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError(isRTL ? 'يرجى إدخال بريد إلكتروني صحيح' : 'Please enter a valid email address');
      return false;
    }

    // Basic phone validation (at least 10 digits)
    const phoneRegex = /^[\d\s\+\-\(\)]{10,}$/;
    if (!phoneRegex.test(formData.phone.replace(/\s/g, ''))) {
      setError(isRTL ? 'يرجى إدخال رقم جوال صحيح' : 'Please enter a valid phone number');
      return false;
    }

    return true;
  };

  const validateStep2 = () => {
    if (!formData.company_name) {
      setError(isRTL ? 'اسم الشركة مطلوب' : 'Company name is required');
      return false;
    }
    return true;
  };

  const validateStep3 = () => {
    if (!formData.plan_id) {
      setError(isRTL ? 'يرجى اختيار خطة' : 'Please select a plan');
      return false;
    }
    return true;
  };

  const handleNext = () => {
    setError(null);

    if (currentStep === 1 && !validateStep1()) return;
    if (currentStep === 2 && !validateStep2()) return;
    if (currentStep === 3 && !validateStep3()) return;

    setCurrentStep(prev => Math.min(prev + 1, 4));
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    setError(null);
  };

  const handleSubmit = async () => {
    if (!validateStep3()) return;

    setLoading(true);
    setError(null);

    try {
      const signupData = {
        email: formData.email,
        password: formData.password,
        name: formData.name,
        phone: formData.phone,
        company_name: formData.company_name,
        company_description: formData.company_description || null,
        plan_id: formData.plan_id,
      };

      const response = await authAPI.clientSignup(signupData);

      // Save token and user to localStorage
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));

      // Check if payment is required based on system settings and plan
      const requiresPayment = response.data.requires_payment === true;

      // Redirect based on payment requirement
      if (requiresPayment) {
        // If trial is disabled and plan requires payment, redirect to payment page
        window.location.href = '/payment';
      } else {
        // If trial is enabled or plan is free, redirect to dashboard
        window.location.href = '/dashboard';
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.response?.data?.details || handleApiError(err);
      console.error('Signup error:', err.response?.data || err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    { number: 1, title: isRTL ? 'المعلومات الشخصية' : 'Personal Info', icon: User },
    { number: 2, title: isRTL ? 'معلومات الشركة' : 'Company Info', icon: Building2 },
    { number: 3, title: isRTL ? 'اختيار الخطة' : 'Choose Plan', icon: CreditCard },
  ];

  if (loading && currentStep === 4) {
    return <Loading fullScreen message={isRTL ? 'جاري إنشاء حسابك...' : 'Creating your account...'} />;
  }

  return (
    <div 
      className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-emerald-50 py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden" 
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      {/* Background Patterns */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-0 right-0 w-96 h-96 bg-sky-200 rounded-full mix-blend-multiply filter blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-emerald-200 rounded-full mix-blend-multiply filter blur-3xl animate-pulse delay-700"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-72 h-72 bg-sky-100 rounded-full mix-blend-multiply filter blur-3xl animate-pulse delay-1000"></div>
      </div>

      {/* Grid Pattern Overlay */}
      <div 
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%230ea5e9' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}
      ></div>

      <div className="max-w-4xl mx-auto relative z-10 animate-in fade-in duration-500">
        {/* Header with Logo and Language Toggle */}
        <div className="flex items-center justify-between mb-8">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 group"
          >
            <div className="w-12 h-12 bg-gradient-to-br from-sky-600 to-emerald-500 rounded-xl flex items-center justify-center text-white shadow-lg group-hover:shadow-sky-500/30 transition-all group-hover:scale-105">
              <Layers size={24} />
            </div>
            <span className="text-2xl font-bold text-slate-900 group-hover:text-sky-600 transition-colors">
              {isRTL ? 'سَلِس' : 'sls'}<span className="text-sky-600">.</span>
            </span>
          </Link>

          <button
            onClick={toggleLanguage}
            className="inline-flex items-center gap-2 px-4 py-2 text-slate-600 hover:text-sky-600 border-2 border-slate-200 hover:border-sky-600 rounded-xl transition-all font-bold text-sm bg-white/80 backdrop-blur-sm shadow-sm hover:shadow-md"
            title={isRTL ? 'Switch to English' : 'التبديل إلى العربية'}
          >
            <Languages className="w-4 h-4" />
            <span>{language === 'ar' ? 'EN' : 'عربي'}</span>
          </button>
        </div>

        {/* Enhanced Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            {steps.map((step, index) => {
              const StepIcon = step.icon;
              const isActive = currentStep === step.number;
              const isCompleted = currentStep > step.number;
              
              return (
                <div key={step.number} className="flex-1 flex items-center">
                  <div className="flex flex-col items-center flex-1">
                    <div className={`relative flex items-center justify-center w-12 h-12 rounded-full border-2 transition-all ${
                      isCompleted
                        ? 'bg-emerald-500 border-emerald-500 text-white'
                        : isActive
                        ? 'bg-sky-600 border-sky-600 text-white shadow-lg shadow-sky-500/30'
                        : 'bg-white border-slate-300 text-slate-400'
                    }`}>
                      {isCompleted ? (
                        <CheckCircle className="w-6 h-6" />
                      ) : (
                        <StepIcon className="w-6 h-6" />
                      )}
                    </div>
                    <span className={`mt-2 text-xs font-bold ${isActive || isCompleted ? 'text-slate-900' : 'text-slate-400'}`}>
                      {step.title}
                    </span>
                  </div>
                  {index < steps.length - 1 && (
                    <div className={`flex-1 h-0.5 mx-2 ${isCompleted ? 'bg-emerald-500' : 'bg-slate-200'}`}></div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="w-full bg-slate-200 rounded-full h-2">
            <div
              className="bg-gradient-to-r from-sky-600 to-emerald-500 h-2 rounded-full transition-all duration-500 shadow-lg"
              style={{ width: `${(currentStep / 3) * 100}%` }}
            />
          </div>
        </div>

        {/* Main Form Card */}
        <Card className="p-8 shadow-2xl border-0 bg-white/90 backdrop-blur-sm">
          {/* Step 1: Personal Information */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-3xl font-bold text-slate-900 mb-2">
                  {isRTL ? 'إنشاء حسابك' : 'Create Your Account'}
                </h2>
                <p className="text-slate-600">
                  {isRTL ? 'ابدأ رحلتك معنا بإنشاء حساب جديد' : 'Start your journey with us by creating a new account'}
                </p>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <label htmlFor="name" className="block text-sm font-bold text-slate-700">
                    {isRTL ? 'الاسم الكامل' : 'Full Name'} *
                  </label>
                  <div className="relative">
                    <div className={`absolute inset-y-0 ${isRTL ? 'right-0 pr-4' : 'left-0 pl-4'} flex items-center pointer-events-none`}>
                      <User className="h-5 w-5 text-slate-400" />
                    </div>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className={`w-full ${isRTL ? 'pr-12 pl-4' : 'pl-12 pr-4'} py-3 border-2 border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-all bg-white text-slate-900 placeholder-slate-400`}
                      placeholder={isRTL ? 'أحمد محمد' : 'John Doe'}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label htmlFor="email" className="block text-sm font-bold text-slate-700">
                    {isRTL ? 'البريد الإلكتروني' : 'Email Address'} *
                  </label>
                  <div className="relative">
                    <div className={`absolute inset-y-0 ${isRTL ? 'right-0 pr-4' : 'left-0 pl-4'} flex items-center pointer-events-none`}>
                      <Mail className="h-5 w-5 text-slate-400" />
                    </div>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`w-full ${isRTL ? 'pr-12 pl-4' : 'pl-12 pr-4'} py-3 border-2 border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-all bg-white text-slate-900 placeholder-slate-400`}
                      placeholder={isRTL ? 'you@company.com' : 'you@company.com'}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label htmlFor="password" className="block text-sm font-bold text-slate-700">
                    {isRTL ? 'كلمة المرور' : 'Password'} *
                  </label>
                  <div className="relative">
                    <div className={`absolute inset-y-0 ${isRTL ? 'right-0 pr-4' : 'left-0 pl-4'} flex items-center pointer-events-none`}>
                      <Lock className="h-5 w-5 text-slate-400" />
                    </div>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      className={`w-full ${isRTL ? 'pl-12 pr-12' : 'pl-12 pr-12'} py-3 border-2 border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-all bg-white text-slate-900 placeholder-slate-400`}
                      placeholder={isRTL ? '12 حرفًا على الأقل' : 'At least 12 characters'}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className={`absolute inset-y-0 ${isRTL ? 'left-0 pl-4' : 'right-0 pr-4'} flex items-center text-slate-400 hover:text-slate-600 transition-colors`}
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <label htmlFor="confirmPassword" className="block text-sm font-bold text-slate-700">
                    {isRTL ? 'تأكيد كلمة المرور' : 'Confirm Password'} *
                  </label>
                  <div className="relative">
                    <div className={`absolute inset-y-0 ${isRTL ? 'right-0 pr-4' : 'left-0 pl-4'} flex items-center pointer-events-none`}>
                      <Lock className="h-5 w-5 text-slate-400" />
                    </div>
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      id="confirmPassword"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      className={`w-full ${isRTL ? 'pl-12 pr-12' : 'pl-12 pr-12'} py-3 border-2 border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-all bg-white text-slate-900 placeholder-slate-400`}
                      placeholder={isRTL ? 'أعد إدخال كلمة المرور' : 'Re-enter your password'}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className={`absolute inset-y-0 ${isRTL ? 'left-0 pl-4' : 'right-0 pr-4'} flex items-center text-slate-400 hover:text-slate-600 transition-colors`}
                    >
                      {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <label htmlFor="phone" className="block text-sm font-bold text-slate-700">
                    {isRTL ? 'رقم الجوال' : 'Phone Number'} *
                  </label>
                  <div className="relative">
                    <div className={`absolute inset-y-0 ${isRTL ? 'right-0 pr-4' : 'left-0 pl-4'} flex items-center pointer-events-none`}>
                      <Phone className="h-5 w-5 text-slate-400" />
                    </div>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className={`w-full ${isRTL ? 'pr-12 pl-4' : 'pl-12 pr-4'} py-3 border-2 border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-all bg-white text-slate-900 placeholder-slate-400`}
                      placeholder={isRTL ? '+966 5XX XXX XXX' : '+966 5XX XXX XXX'}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Company Information */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-3xl font-bold text-slate-900 mb-2">
                  {isRTL ? 'معلومات الشركة' : 'Company Information'}
                </h2>
                <p className="text-slate-600">
                  {isRTL ? 'أخبرنا المزيد عن شركتك' : 'Tell us more about your company'}
                </p>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <label htmlFor="company_name" className="block text-sm font-bold text-slate-700">
                    {isRTL ? 'اسم الشركة' : 'Company Name'} *
                  </label>
                  <div className="relative">
                    <div className={`absolute inset-y-0 ${isRTL ? 'right-0 pr-4' : 'left-0 pl-4'} flex items-center pointer-events-none`}>
                      <Building2 className="h-5 w-5 text-slate-400" />
                    </div>
                    <input
                      type="text"
                      id="company_name"
                      name="company_name"
                      value={formData.company_name}
                      onChange={handleChange}
                      className={`w-full ${isRTL ? 'pr-12 pl-4' : 'pl-12 pr-4'} py-3 border-2 border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-all bg-white text-slate-900 placeholder-slate-400`}
                      placeholder={isRTL ? 'اسم الشركة' : 'Acme Corporation'}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label htmlFor="company_description" className="block text-sm font-bold text-slate-700">
                    {isRTL ? 'وصف الشركة (اختياري)' : 'Company Description (Optional)'}
                  </label>
                  <div className="relative">
                    <div className={`absolute ${isRTL ? 'right-4 top-4' : 'left-4 top-4'} pointer-events-none`}>
                      <FileText className="h-5 w-5 text-slate-400" />
                    </div>
                    <textarea
                      id="company_description"
                      name="company_description"
                      value={formData.company_description}
                      onChange={handleChange}
                      rows={4}
                      className={`w-full ${isRTL ? 'pr-12 pl-4' : 'pl-12 pr-4'} py-3 border-2 border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-all bg-white text-slate-900 placeholder-slate-400 resize-none`}
                      placeholder={isRTL ? 'أخبرنا المزيد عن شركتك...' : 'Tell us about your company...'}
                    />
                  </div>
                </div>

              </div>
            </div>
          )}

          {/* Step 3: Plan Selection */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-3xl font-bold text-slate-900 mb-2">
                  {isRTL ? 'اختر خطتك' : 'Choose Your Plan'}
                </h2>
                <p className="text-slate-600">
                  {isRTL ? 'اختر الخطة التي تناسب احتياجاتك' : 'Select the plan that fits your needs'}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {plans.map((plan) => {
                  const rawBasePrice = plan.monthly_price !== null && plan.monthly_price !== undefined
                    ? plan.monthly_price
                    : plan.price || 0;
                  
                  const basePrice = parseFloat(rawBasePrice) || 0;
                  const discount = parseFloat(plan.monthly_discount) || 0;
                  const finalPrice = discount > 0 && basePrice > 0
                    ? Math.round(basePrice * (1 - discount / 100) * 100) / 100
                    : basePrice;

                  const hasDiscount = discount > 0 && basePrice > 0;
                  const isSelected = formData.plan_id === plan.id;

                  return (
                    <div
                      key={plan.id}
                      onClick={() => setFormData({ ...formData, plan_id: plan.id })}
                      className={`relative p-6 border-2 rounded-2xl cursor-pointer transition-all hover:shadow-xl ${
                        isSelected
                          ? 'border-sky-600 bg-gradient-to-br from-sky-50 to-emerald-50 ring-2 ring-sky-600 ring-offset-2 shadow-lg scale-105'
                          : 'border-slate-200 hover:border-sky-300 bg-white'
                      }`}
                    >
                      {/* Selected Badge */}
                      {isSelected && (
                        <div className={`absolute -top-3 ${isRTL ? 'left-4' : 'right-4'} bg-gradient-to-r from-sky-600 to-emerald-500 text-white px-4 py-1 rounded-full text-xs font-bold shadow-lg`}>
                          {isRTL ? 'محدد' : 'Selected'}
                        </div>
                      )}

                      {/* Discount Badge */}
                      {hasDiscount && (
                        <div className={`absolute -top-3 ${isRTL ? 'right-4' : 'left-4'} bg-red-500 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg`}>
                          {discount}% {isRTL ? 'خصم' : 'OFF'}
                        </div>
                      )}

                      <div className="text-center mb-4">
                        <h3 className="text-xl font-bold text-slate-900 mb-1">
                          {plan.name}
                        </h3>
                        {plan.name_ar && (
                          <p className="text-sm text-slate-600 mb-3">{plan.name_ar}</p>
                        )}
                        
                        {/* Pricing */}
                        <div className="mb-3">
                          {hasDiscount && (
                            <div className="text-sm text-slate-500 line-through mb-1">
                              ${basePrice.toFixed(2)}
                            </div>
                          )}
                          <div className="text-3xl font-bold bg-gradient-to-r from-sky-600 to-emerald-500 bg-clip-text text-transparent">
                            ${finalPrice.toFixed(2)}
                            <span className="text-sm text-slate-500 font-normal ml-1">
                              {isRTL ? '/شهر' : '/month'}
                            </span>
                          </div>
                        </div>
                        
                        {plan.description && (
                          <p className="text-sm text-slate-600 mb-4 line-clamp-2">
                            {plan.description}
                          </p>
                        )}
                      </div>

                      {/* Limits */}
                      <div className={`space-y-2 text-sm mb-4 ${isRTL ? 'text-right' : 'text-left'}`}>
                        {plan.max_posts !== null && plan.max_posts !== undefined ? (
                          <div className="flex items-center">
                            <CheckCircle className={`w-4 h-4 text-emerald-500 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                            <span>{isRTL ? `حتى ${plan.max_posts.toLocaleString()} منشور` : `Up to ${plan.max_posts.toLocaleString()} posts`}</span>
                          </div>
                        ) : (
                          <div className="flex items-center">
                            <CheckCircle className={`w-4 h-4 text-emerald-500 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                            <span>{isRTL ? 'منشورات غير محدودة' : 'Unlimited posts'}</span>
                          </div>
                        )}
                        {plan.max_users !== null && plan.max_users !== undefined ? (
                          <div className="flex items-center">
                            <CheckCircle className={`w-4 h-4 text-emerald-500 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                            <span>{isRTL ? `حتى ${plan.max_users.toLocaleString()} مستخدم` : `Up to ${plan.max_users.toLocaleString()} users`}</span>
                          </div>
                        ) : (
                          <div className="flex items-center">
                            <CheckCircle className={`w-4 h-4 text-emerald-500 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                            <span>{isRTL ? 'مستخدمين غير محدودين' : 'Unlimited users'}</span>
                          </div>
                        )}
                        {plan.max_clients !== null && plan.max_clients !== undefined ? (
                          <div className="flex items-center">
                            <CheckCircle className={`w-4 h-4 text-emerald-500 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                            <span>{isRTL ? `حتى ${plan.max_clients.toLocaleString()} عميل` : `Up to ${plan.max_clients.toLocaleString()} clients`}</span>
                          </div>
                        ) : (
                          <div className="flex items-center">
                            <CheckCircle className={`w-4 h-4 text-emerald-500 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                            <span>{isRTL ? 'عملاء غير محدودين' : 'Unlimited clients'}</span>
                          </div>
                        )}
                      </div>

                      {/* Dynamic Features */}
                      {plan.features && Array.isArray(plan.features) && plan.features.length > 0 && (
                        <div className={`pt-4 border-t border-slate-200 space-y-2 text-sm ${isRTL ? 'text-right' : 'text-left'}`}>
                          {plan.features.slice(0, 4).map((feature) => (
                            <div key={feature.id} className="flex items-center">
                              {feature.icon ? (
                                <span className={isRTL ? 'ml-2' : 'mr-2'}>{feature.icon}</span>
                              ) : (
                                <CheckCircle className={`w-4 h-4 text-emerald-500 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                              )}
                              <span className="text-slate-700">
                                {feature.name}
                              </span>
                            </div>
                          ))}
                          {plan.features.length > 4 && (
                            <div className="text-xs text-slate-500 mt-2">
                              +{plan.features.length - 4} {isRTL ? 'ميزات إضافية' : 'more features'}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mt-6 rounded-xl bg-red-50 border-2 border-red-200 p-4 animate-in slide-in-from-top duration-300">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-800 font-medium">{error}</p>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="mt-8 flex justify-between items-center">
            <div>
              {currentStep > 1 && (
                <Button
                  onClick={handleBack}
                  variant="secondary"
                  className="border-2"
                >
                  {isRTL ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
                  {isRTL ? 'السابق' : 'Back'}
                </Button>
              )}
            </div>
            <div>
              {currentStep < 3 ? (
                <Button
                  onClick={handleNext}
                  variant="primary"
                  className="bg-gradient-to-r from-sky-600 to-emerald-500 hover:from-sky-700 hover:to-emerald-600 text-white shadow-lg shadow-sky-500/30"
                >
                  {isRTL ? 'التالي' : 'Next'}
                  {isRTL ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={loading}
                  variant="primary"
                  className="bg-gradient-to-r from-sky-600 to-emerald-500 hover:from-sky-700 hover:to-emerald-600 text-white shadow-lg shadow-sky-500/30"
                  loading={loading}
                >
                  {loading ? (isRTL ? 'جاري الإنشاء...' : 'Creating Account...') : (isRTL ? 'إنشاء الحساب' : 'Create Account')}
                </Button>
              )}
            </div>
          </div>

          {/* Login Link */}
          <div className="mt-6 text-center text-sm text-slate-600">
            {isRTL ? 'لديك حساب بالفعل؟' : 'Already have an account?'}{' '}
            <Link to="/login" className="font-bold text-sky-600 hover:text-sky-700 transition-colors">
              {isRTL ? 'تسجيل الدخول' : 'Sign in'}
            </Link>
          </div>
        </Card>

        {/* Back to Home Link */}
        <div className="mt-6 text-center">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 transition-colors font-medium"
          >
            <Home className="w-4 h-4" />
            {isRTL ? 'العودة للصفحة الرئيسية' : 'Back to Home'}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SignupWizard;
